import { useState, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Scale,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { formatCurrency } from "@/lib/billingUtils";

interface AccountBalance {
  id: string;
  code: string;
  name: string;
  account_type: string;
  total_debit: number;
  total_credit: number;
  balance: number;
}

export default function TrialBalance() {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(0, 1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Fetch account balances from journal entries
  const { data: balances = [], isLoading } = useQuery<AccountBalance[]>({
    queryKey: ["trial-balance", dateFrom, dateTo],
    queryFn: async () => {
      // Get all journal entry lines within date range
      const { data: lines, error } = await api
        .from("journal_entry_lines")
        .select(`
          account_id,
          debit,
          credit,
          accounting_accounts!inner(id, code, name, account_type),
          journal_entries!inner(entry_date, status)
        `)
        .eq("journal_entries.status", "posted")
        .gte("journal_entries.entry_date", dateFrom)
        .lte("journal_entries.entry_date", dateTo);

      if (error) throw error;

      // Aggregate by account
      const accountMap = new Map<string, AccountBalance>();

      (lines || []).forEach((line: any) => {
        const acc = line.accounting_accounts;
        if (!acc) return;

        if (!accountMap.has(acc.id)) {
          accountMap.set(acc.id, {
            id: acc.id,
            code: acc.code,
            name: acc.name,
            account_type: acc.account_type,
            total_debit: 0,
            total_credit: 0,
            balance: 0,
          });
        }

        const entry = accountMap.get(acc.id)!;
        entry.total_debit += line.debit || 0;
        entry.total_credit += line.credit || 0;
        entry.balance = entry.total_debit - entry.total_credit;
      });

      // Convert to array and sort by code
      return Array.from(accountMap.values())
        .filter(a => a.total_debit !== 0 || a.total_credit !== 0)
        .sort((a, b) => a.code.localeCompare(b.code));
    },
  });

  // Calculate totals
  const totals = useMemo(() => {
    const totalDebit = balances.reduce((sum, a) => sum + a.total_debit, 0);
    const totalCredit = balances.reduce((sum, a) => sum + a.total_credit, 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    return { totalDebit, totalCredit, isBalanced };
  }, [balances]);

  // Group by account type for summary
  const summary = useMemo(() => {
    const groups: Record<string, { debit: number; credit: number; balance: number }> = {};

    balances.forEach(acc => {
      const group = acc.code.charAt(0); // First digit = group
      if (!groups[group]) {
        groups[group] = { debit: 0, credit: 0, balance: 0 };
      }
      groups[group].debit += acc.total_debit;
      groups[group].credit += acc.total_credit;
      groups[group].balance += acc.balance;
    });

    return groups;
  }, [balances]);

  const groupNames: Record<string, string> = {
    '1': 'Financiacion basica',
    '2': 'Activo no corriente',
    '3': 'Existencias',
    '4': 'Acreedores y deudores',
    '5': 'Cuentas financieras',
    '6': 'Compras y gastos',
    '7': 'Ventas e ingresos',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Balance de Comprobacion</h1>
            <p className="text-muted-foreground">
              Sumas y saldos de todas las cuentas
            </p>
          </div>
          {balances.length > 0 && (
            <Badge
              variant={totals.isBalanced ? "default" : "destructive"}
              className="text-lg px-4 py-2 gap-2"
            >
              {totals.isBalanced ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Cuadrado
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5" />
                  Descuadre: {formatCurrency(Math.abs(totals.totalDebit - totals.totalCredit))}
                </>
              )}
            </Badge>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Desde</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Hasta</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <div className="text-sm text-muted-foreground">
                  {balances.length} cuentas con movimientos
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary by Group */}
        {Object.keys(summary).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Resumen por Grupos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {Object.entries(summary).sort().map(([group, data]) => (
                  <div key={group} className="p-3 rounded-lg bg-muted/50 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Grupo {group}</p>
                    <p className="text-sm font-medium truncate" title={groupNames[group]}>
                      {groupNames[group]?.split(' ')[0] || group}
                    </p>
                    <p className={`text-lg font-bold ${data.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(data.balance))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {data.balance >= 0 ? 'Deudor' : 'Acreedor'}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Balance Table */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-12">Cargando...</div>
            ) : balances.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Scale className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay movimientos en este periodo</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Codigo</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead className="w-[140px] text-right">Debe</TableHead>
                    <TableHead className="w-[140px] text-right">Haber</TableHead>
                    <TableHead className="w-[140px] text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balances.map((acc) => (
                    <TableRow key={acc.id}>
                      <TableCell className="font-mono font-medium">{acc.code}</TableCell>
                      <TableCell>{acc.name}</TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {acc.total_debit > 0 ? formatCurrency(acc.total_debit) : ''}
                      </TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {acc.total_credit > 0 ? formatCurrency(acc.total_credit) : ''}
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        <span className={acc.balance >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(Math.abs(acc.balance))}
                          {acc.balance >= 0 ? ' D' : ' H'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow className="font-bold bg-muted/50 text-lg">
                    <TableCell colSpan={2} className="text-right">TOTALES</TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {formatCurrency(totals.totalDebit)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      {formatCurrency(totals.totalCredit)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {totals.isBalanced ? (
                        <span className="text-blue-600">CUADRADO</span>
                      ) : (
                        <span className="text-orange-600">
                          {formatCurrency(Math.abs(totals.totalDebit - totals.totalCredit))}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
